/* Inline sell grid — window.SellGrid.
 *
 * The printing-picker's tile+counter UX, rendered directly onto a page instead of
 * into a modal. One tile per PRINTING, one counter per finish that printing comes
 * in; every tap writes straight through to window.SellDraft, so the existing
 * bottom drawer is the running total and there is nothing to "commit".
 *
 * Two entry points, because two surfaces need it:
 *   renderInto(host, docs, opts) — draw tiles from docs the caller already has
 *                                  (the card-search page's sell view; no refetch)
 *   mount(host, opts)            — self-contained: own search box + /api/search
 *                                  (the sell page's "add from our catalog" block)
 *
 * Printing identity and finish fallbacks come from assets/printing-utils.js, shared
 * with the modal picker so the two can't disagree about what one printing is.
 */
window.SellGrid = window.SellGrid || (function () {
  var U = null;
  function utils() { return U || (U = window.PrintingUtils); }
  function draft() { return window.SellDraft; }
  function esc(s) { return utils().esc(s); }

  /* ── Draft reads/writes ──────────────────────────────────────────────────── */

  // qty by SellDraft key, rebuilt per sync (the list is tens of lines at most).
  function qtyMap() {
    var m = {}, d = draft();
    if (!d) return m;
    d.list().forEach(function (it) { m[it.key || d.keyOf(it)] = it.qty || 1; });
    return m;
  }

  // Add/remove one copy of a printing+finish. SellDraft.add() increments an
  // existing line, so the zero→one case goes through add() and every later step
  // through setQty(); dropping to zero removes the line entirely.
  function bump(line, delta) {
    var d = draft(); if (!d) return;
    var k = d.keyOf(line), cur = qtyMap()[k] || 0, next = cur + delta;
    if (next <= 0) { if (cur > 0) d.remove(k); return; }
    if (cur === 0) d.add(line); else d.setQty(k, next);
  }

  /* ── Counter sync ────────────────────────────────────────────────────────────
   * Counters re-read their value from the draft rather than tracking it locally,
   * so they survive a re-render, pagination, and edits made anywhere else — remove
   * a line from the drawer and the tile's ×N clears itself.
   *
   * Subscribed exactly once and driven off the live DOM, because SellDraft.onChange
   * has no unsubscribe: registering per render would stack a new subscriber on every
   * search and leak them for the life of the page.
   */
  var subscribed = false;
  function subscribe() {
    var d = draft();
    if (subscribed || !d) return;
    subscribed = true;
    d.onChange(function () { syncCounters(); });
  }
  function syncCounters(root) {
    var m = qtyMap();
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll('.sg-finrow[data-key]'), function (row) {
      var q = m[row.getAttribute('data-key')] || 0;
      var btn = row.querySelector('.sg-add'), minus = row.querySelector('.sg-minus');
      var label = row.getAttribute('data-label') || '';
      if (btn) btn.textContent = q ? (label + ' ×' + q) : (label + ' +');
      if (minus) minus.style.display = q ? '' : 'none';
    });
    // Outline any tile with at least one counted copy.
    Array.prototype.forEach.call(scope.querySelectorAll('.sg-tile'), function (tile) {
      var any = Array.prototype.some.call(tile.querySelectorAll('.sg-finrow[data-key]'), function (row) {
        return (m[row.getAttribute('data-key')] || 0) > 0;
      });
      tile.classList.toggle('sg-picked', any);
    });
  }

  /* ── Tiles ───────────────────────────────────────────────────────────────── */

  // `slots` is the grid's shared finish plan, keyed by game (see finishSlotsByGame) —
  // one row per finish any printing of that game comes in, same order on every tile.
  function tileFor(doc, opts, slots) {
    var u = utils();
    var tile = document.createElement('div');
    tile.className = 'sg-tile';

    var art = document.createElement('div');
    art.className = 'sg-art';
    if (doc.image_url) {
      var img = document.createElement('img');
      img.src = doc.image_url; img.alt = doc.name || ''; img.loading = 'lazy';
      art.appendChild(img);
      // Double-faced printings flip in place — identifying which face you're holding
      // is exactly the question a seller has in front of a grid of variants.
      if (doc.back_image_url) {
        var front = doc.image_url, flipped = false;
        var fb = document.createElement('button');
        fb.type = 'button'; fb.className = 'sg-flip'; fb.title = 'Flip card'; fb.setAttribute('aria-label', 'Flip card');
        fb.textContent = '⟲';
        fb.onclick = function (e) { e.stopPropagation(); flipped = !flipped; img.src = flipped ? doc.back_image_url : front; };
        art.appendChild(fb);
      }
    } else {
      // Card back rather than a text tile — a seller scanning this grid is matching
      // against physical cards, so a card-shaped stand-in reads far better than a gap.
      art.appendChild(TTT.cardPlaceholder(doc.game, { name: doc.name, sub: doc.set_name || '' }));
    }
    tile.appendChild(art);

    // Name/set/treatment ride in one block, because the tile's children are its grid
    // rows: a tile with no treatment tag would otherwise be one row short and put its
    // counters on the row its neighbours use for something else.
    var meta = document.createElement('div');
    meta.className = 'sg-meta';
    tile.appendChild(meta);

    var nm = document.createElement('div');
    nm.className = 'sg-cap'; nm.textContent = doc.name || '';
    meta.appendChild(nm);

    var sub = document.createElement('div');
    sub.className = 'sg-nm';
    sub.textContent = (doc.set_name || '?') + (doc.collector_number ? ' · #' + doc.collector_number : '');
    meta.appendChild(sub);

    // Every treatment, not just the first. A borderless surge foil used to render as
    // plain "BORDERLESS" — dropping the half that actually drives the price.
    var treatHtml = u.treatmentHtml(u.treatmentsOf(doc), 2, { color: 'inherit' });
    if (treatHtml) {
      var t = document.createElement('div');
      t.className = 'sg-treat'; t.innerHTML = treatHtml;
      meta.appendChild(t);
    }

    var game = u.gameOf(doc);
    if (!game || (window.TTT && TTT.buylist && !TTT.buylist.buysOnline(game))) {
      // Which games the website buys is a backend setting (Buylist → Config → Pricing →
      // "Buy online"). Anything else would take a count we can't quote, so say so
      // instead of offering a dead counter.
      var nb = document.createElement('div');
      nb.className = 'sg-nobuy';
      nb.textContent = 'We buy this in store only — bring it to the counter for an offer.';
      tile.appendChild(nb);
      return tile;
    }

    // A set we've closed to buying: say so on the tile instead of offering counters
    // that the quote endpoint would only refuse a moment later.
    var blockNote = (window.TTT && TTT.buylist) ? TTT.buylist.note(game, doc.set_code) : '';
    if (blockNote) {
      var bn = document.createElement('div');
      bn.className = 'sg-nobuy';
      bn.textContent = blockNote;
      tile.appendChild(bn);
      return tile;
    }

    var fins = document.createElement('div');
    fins.className = 'sg-fins';
    var own = u.finishesFor(doc), have = {};
    own.forEach(function (f) { have[f] = 1; });
    var plan = (slots && slots[game]) || [];
    // Empty rows are kept, including trailing ones: with the block anchored to the
    // tile's floor, a missing row has to hold its place or everything below it shifts.
    (plan.length ? plan : own).forEach(function (fin) {
      if (have[fin]) fins.appendChild(counterRow(doc, fin, game, opts));
      else fins.appendChild(emptySlot());
    });
    tile.appendChild(fins);
    return tile;
  }

  function emptySlot() {
    var slot = document.createElement('div');
    slot.className = 'sg-finslot';
    slot.setAttribute('aria-hidden', 'true');
    return slot;
  }

  function counterRow(doc, fin, game, opts) {
    var u = utils();
    var line = u.lineFor(doc, fin, game, 1);
    // "Surge Foil" rather than a bare "Foil" when this printing's foil layer has a name —
    // the tile's treatment chip sits above BOTH buttons, so without this the seller can't
    // tell which of the two copies it describes (see PrintingUtils.finLabelFor).
    var label = u.finLabelFor ? u.finLabelFor(fin, u.treatmentsOf(doc)) : u.finLabel(fin);
    var row = document.createElement('div');
    row.className = 'sg-finrow';
    row.setAttribute('data-key', draft() ? draft().keyOf(line) : '');
    row.setAttribute('data-label', label);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sg-add' + (u.isFoilish(fin) ? ' sg-add-foil' : '');
    btn.textContent = label + ' +';

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'sg-minus'; minus.textContent = '−';
    minus.style.display = 'none';
    minus.setAttribute('aria-label', 'Remove one ' + label + ' ' + (doc.name || ''));

    function tap(delta) {
      // lineFor is rebuilt per tap so the object handed to SellDraft is never one it
      // already owns (add() mutates the line it stores).
      bump(u.lineFor(doc, fin, game, 1), delta);
      if (opts && opts.onChange) opts.onChange();
    }
    btn.onclick = function () { tap(1); };
    minus.onclick = function () { tap(-1); };

    row.appendChild(btn); row.appendChild(minus);
    return row;
  }

  /* ── Ordering ────────────────────────────────────────────────────────────────
   * Under a relevance sort the server ranks by text match, which is a TIE across
   * every printing of one card — they all carry the same name — so the printings of
   * a card come back in effectively arbitrary order, interleaved with other cards.
   *
   * So keep the server's relevance ranking ACROSS cards (first appearance wins) and
   * impose a real order WITHIN each card: newest printing first, collector number as
   * the tiebreak. That's the same rule the modal picker uses, and the one a seller
   * needs — they're usually holding a recent printing, and a card's printings should
   * sit together rather than scattered down the grid.
   *
   * Only correct for relevance. An explicit sort (price, name, release date) is a
   * total order the seller asked for, and regrouping would fight it.
   */
  function groupPrintingsByCard(docs) {
    var groups = [], byName = {};
    (docs || []).forEach(function (d) {
      var k = String(d.name || '').toLowerCase();
      if (!byName[k]) { byName[k] = []; groups.push(byName[k]); }
      byName[k].push(d);
    });
    groups.forEach(function (g) {
      g.sort(function (a, b) {
        var da = +a.released_at || 0, db = +b.released_at || 0;
        if (da !== db) return db - da;
        return (parseInt(a.collector_number, 10) || 0) - (parseInt(b.collector_number, 10) || 0);
      });
    });
    return [].concat.apply([], groups);
  }

  /* ── Public: render a set of docs ────────────────────────────────────────── */

  function renderInto(host, docs, opts) {
    if (!host) return;
    opts = opts || {};
    // Tiles decide buy-online vs in-store from the buylist config. Drawn before it
    // lands, every game but Magic and Pokémon would read "in store only" until the
    // next search, so wait for a load that's already underway (it's one small request).
    var BL = window.TTT && TTT.buylist;
    var pending = BL && !BL.loaded() && BL.ready && BL.ready();
    if (pending) {
      host.innerHTML = '<div class="sg-note"><span class="sg-spin"></span>Finding printings…</div>';
      pending.then(function () { renderInto(host, docs, opts); });
      return;
    }
    subscribe();
    host.innerHTML = '';
    var list = utils().dedupePrintings(docs || []);
    if (opts.groupPrintings) list = groupPrintingsByCard(list);
    if (!list.length) {
      host.innerHTML = '<div class="sg-note">' + esc(opts.emptyText || 'No printings found.') + '</div>';
      return;
    }
    var grid = document.createElement('div');
    grid.className = 'sg-grid';
    var slots = utils().finishSlotsByGame(list);
    // Row budget for the tiles: art + meta + one row per finish. Where subgrid is
    // supported the tiles share the grid's rows, so a row of the grid holding no
    // etched printing collapses that row to nothing instead of reserving it — the
    // finish rows only line up within a row of tiles, which is where it's read.
    // Widest game wins, since one tile can't span fewer rows than its neighbours.
    var most = 0;
    Object.keys(slots).forEach(function (g) { most = Math.max(most, slots[g].length); });
    grid.style.setProperty('--sg-slots', String(most));
    grid.style.setProperty('--sg-span', String(most + 2));
    list.forEach(function (d) { grid.appendChild(tileFor(d, opts, slots)); });
    host.appendChild(grid);
    syncCounters(host);
  }

  /* ── Public: self-contained search + grid ────────────────────────────────── */

  function mount(host, opts) {
    if (!host) return;
    opts = opts || {};
    var apiBase = String(opts.apiBase || '').replace(/\/+$/, '');
    subscribe();

    host.innerHTML =
      '<div class="sg-search">' +
        '<div class="sg-qwrap">' +
          '<input type="search" class="sg-q" autocomplete="off" placeholder="' + esc(opts.placeholder || 'Search a card you want to sell…') + '">' +
          '<div class="sg-ac" role="listbox"></div>' +
        '</div>' +
        '<button type="button" class="sg-go">Search</button>' +
      '</div>' +
      '<div class="sg-sortbar">' +
        '<label>Sort ' +
          '<select class="sg-sort">' +
            '<option value="relevance">Relevance</option>' +
            '<option value="released">Newest printing</option>' +
            '<option value="popularity">Most played</option>' +
            '<option value="price_desc">Value: high to low</option>' +
            '<option value="name">Name: A → Z</option>' +
          '</select>' +
        '</label>' +
      '</div>' +
      '<div class="sg-out"></div>';
    var input = host.querySelector('.sg-q'), out = host.querySelector('.sg-out'), ac = host.querySelector('.sg-ac');
    var sortSel = host.querySelector('.sg-sort');

    if (!apiBase) {
      out.innerHTML = '<div class="sg-note">Card search isn’t configured yet.</div>';
      return;
    }
    // Start the buylist config (which games and sets we buy) now, so renderInto has it.
    if (window.TTT && TTT.buylist) TTT.buylist.load(apiBase);

    var gen = 0;
    function run() {
      var term = (input.value || '').trim();
      closeAc();
      if (!term) { out.innerHTML = ''; return; }
      var mine = ++gen;
      var sort = (sortSel && sortSel.value) || 'relevance';
      out.innerHTML = '<div class="sg-note"><span class="sg-spin"></span>Finding printings…</div>';
      // No instock filter, deliberately: we buy printings we've never stocked, so the
      // sell grid searches the whole catalog rather than the shelf.
      // game=all to match the typeahead: /api/search defaults an absent `game` to MTG,
      // so without this, picking a Pokémon suggestion returned an empty grid. The
      // buylist prices both games, so both belong here.
      // Sell context: include not-yet-released printings so a seller holding one sees
      // it (greyed, with the reason) rather than an empty result.
      fetch(apiBase + '/api/search?per_page=60&game=all&include_unreleased=1&sort=' + encodeURIComponent(sort) + '&q=' + encodeURIComponent(term))
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (mine !== gen) return;   // superseded by a newer search
          var docs = (res && res.results) || [];
          renderInto(out, docs, {
            onChange: opts.onChange,
            groupPrintings: sort === 'relevance',
            emptyText: 'No cards found for “' + term + '”.'
          });
        })
        .catch(function () { if (mine === gen) out.innerHTML = '<div class="sg-note">Couldn’t load printings.</div>'; });
    }

    /* Typeahead. Same index the grid searches, deduped by card NAME — the index is
       one doc per printing, so without the collapse the dropdown is eight rows of the
       same card. No prices: a buy-side price is the wrong number to show a seller. */
    var acItems = [], acIndex = -1, acGen = 0, acTimer = null;
    function closeAc() { acGen++; ac.classList.remove('open'); ac.innerHTML = ''; acItems = []; acIndex = -1; }
    function setActive(i) {
      acIndex = i;
      Array.prototype.forEach.call(ac.children, function (n, k) { n.classList.toggle('sg-ac-active', k === i); });
      if (ac.children[i]) ac.children[i].scrollIntoView({ block: 'nearest' });
    }
    // Picking a row always searches the REAL card name, so every printing resolves —
    // including the reskins, which then render with their printed name on the tile.
    function choose(realName) { input.value = realName; closeAc(); run(); }
    function fetchAc(term) {
      var mine = ++acGen;
      fetch(apiBase + '/api/search?per_page=40&game=all&include_unreleased=1&q=' + encodeURIComponent(term))
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (mine !== acGen) return;
          var rows = utils().vanityAcRows((res && res.results) || [], term, 8);
          acItems = rows; acIndex = -1;
          ac.innerHTML = '';
          if (!rows.length) { ac.classList.remove('open'); return; }
          rows.forEach(function (r, i) {
            var it = document.createElement('div');
            it.className = 'sg-ac-item' + (r.vanity ? ' sg-ac-vanity' : ''); it.setAttribute('role', 'option');
            // The 30x42 thumb already reserves its space, so this is cosmetic rather than a layout
            // fix — but a card back beats an empty grey box in a list you're scanning quickly.
            var acImg = r.image || TTT.cardBack(r.game);
            it.innerHTML = (acImg ? '<img src="' + esc(acImg) + '" alt="" loading="lazy">' : '<span class="sg-ac-noimg"></span>') +
              '<span class="sg-ac-name">' + esc(r.label) + '</span>';
            it.onmousedown = function (e) { e.preventDefault(); choose(r.name); };  // mousedown beats the input's blur
            it.onmouseenter = function () { setActive(i); };
            ac.appendChild(it);
          });
          ac.classList.add('open');
        })
        .catch(function () { if (mine === acGen) closeAc(); });
    }

    input.addEventListener('input', function () {
      var term = (input.value || '').trim();
      clearTimeout(acTimer);
      if (term.length < 2) { closeAc(); return; }
      acTimer = setTimeout(function () { fetchAc(term); }, 180);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' && acItems.length) { e.preventDefault(); setActive(Math.min(acIndex + 1, acItems.length - 1)); return; }
      if (e.key === 'ArrowUp' && acItems.length) { e.preventDefault(); setActive(Math.max(acIndex - 1, 0)); return; }
      if (e.key === 'Escape') { closeAc(); return; }
      if (e.key === 'Enter') {
        e.preventDefault();
        // A highlighted suggestion wins; a bare Enter still runs the typed text.
        if (acIndex >= 0 && acItems[acIndex]) choose(acItems[acIndex].name); else run();
      }
    });
    input.addEventListener('blur', function () { setTimeout(closeAc, 120); });

    host.querySelector('.sg-go').onclick = run;
    // Re-run rather than re-sort locally: the page holds one page of results, so a
    // client-side sort would only reorder the first 60 of a larger match set.
    if (sortSel) sortSel.onchange = function () { if ((input.value || '').trim()) run(); };
  }

  return { renderInto: renderInto, mount: mount, syncCounters: syncCounters };
})();
